/**
 * R-09 — Journey Detail Page
 * Route: /reports/journeys/:journeyId
 * Two tabs: Learner Enrollments + Badge Earners (both PII-gated server-side)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Award, Download, Activity } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PER_PAGE = 50;

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(n) { return (n ?? 0).toLocaleString(); }

// ── Shared sub-components ────────────────────────────────────────────────────
function KpiCard({ label, value, accent, testId }) {
  return (
    <div className="stat-card" data-testid={testId} style={{ borderTop: `3px solid ${accent}` }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

function RequiredProgressCell({ requiredCompleted, requiredTotal }) {
  if (!requiredTotal) {
    return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
  }
  const pct   = Math.min(100, Math.round((requiredCompleted / requiredTotal) * 100));
  const color = pct >= 70 ? '#22c55e' : pct >= 40 ? '#f59e0b' : '#94a3b8';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: pct === 100 ? '#15803d' : '#1e293b' }}>
          {requiredCompleted}
        </span>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>/ {requiredTotal} required</span>
      </div>
      <div style={{ height: 5, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .3s ease' }} />
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    completed:   { label: 'Completed',   bg: '#dcfce7', color: '#15803d' },
    in_progress: { label: 'In Progress', bg: '#fef3c7', color: '#b45309' },
    not_started: { label: 'Not Started', bg: '#f1f5f9', color: '#64748b' },
  };
  const s = map[status] || map.not_started;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 12,
      fontSize: 11, fontWeight: 600, background: s.bg, color: s.color, whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      padding: '12px 0', borderTop: '1px solid #e2e8f0',
    }}>
      <button
        onClick={() => onPage(page - 1)}
        disabled={page <= 1}
        style={{ padding: '5px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: page <= 1 ? 'not-allowed' : 'pointer', color: page <= 1 ? '#94a3b8' : '#374151', fontSize: 12 }}
      >Prev</button>
      <span style={{ fontSize: 13, color: '#64748b' }}>Page {page} of {totalPages}</span>
      <button
        onClick={() => onPage(page + 1)}
        disabled={page >= totalPages}
        style={{ padding: '5px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: page >= totalPages ? 'not-allowed' : 'pointer', color: page >= totalPages ? '#94a3b8' : '#374151', fontSize: 12 }}
      >Next</button>
    </div>
  );
}

function ExportBtn({ onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 7,
        background: '#fff', cursor: 'pointer', fontSize: 12, color: '#217346', fontWeight: 600,
      }}
    >
      <Download size={13} /> Export CSV
    </button>
  );
}

// ── Tab 1: Learner Enrollments ────────────────────────────────────────────────
function EnrollmentsTab({ journeyId, stats, sortBy, setSortBy, sortDir, setSortDir, onFilterSummary }) {
  const navigate = useNavigate();
  const [rows, setRows]             = useState([]);
  const [statusFilter, setStatus]   = useState('all');
  const [page, setPage]             = useState(1);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(false);
  const [dataSource, setDataSource] = useState(null);

  // Search & partner filter
  const [searchInput, setSearchInput]     = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [partner, setPartner]             = useState('');
  const [partners, setPartners]           = useState([]);

  // Load partner list for dropdown once on mount
  useEffect(() => {
    axios.get(`${API}/api/journeys/${journeyId}/enrollments/partners`, { withCredentials: true })
      .then(r => setPartners(r.data.partners || []))
      .catch(() => {});
  }, [journeyId]);

  const load = useCallback(async (p, s, sb, sd, srch, ptnr) => {
    setLoading(true);
    try {
      const params = { page: p, per_page: PER_PAGE };
      if (s && s !== 'all') params.status = s;
      if (sb) { params.sort_by = sb; params.sort_dir = sd || 'desc'; }
      if (srch) params.search = srch;
      if (ptnr) params.partner = ptnr;
      const { data } = await axios.get(`${API}/api/journeys/${journeyId}/enrollments`, { params, withCredentials: true });
      setRows(data.data || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPage(p);
      setDataSource(data.source || null);
      onFilterSummary(data.status_counts || null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [journeyId, onFilterSummary]);

  useEffect(() => { load(1, 'all', null, 'desc', '', ''); }, [load]);

  const handleStatusChange = s => { setStatus(s); load(1, s, sortBy, sortDir, appliedSearch, partner); };

  const handleSort = col => {
    const newDir = sortBy === col && sortDir === 'desc' ? 'asc' : 'desc';
    setSortBy(col);
    setSortDir(newDir);
    load(1, statusFilter, col, newDir, appliedSearch, partner);
  };

  const handleSearchSubmit = () => {
    const s = searchInput.trim();
    setAppliedSearch(s);
    load(1, statusFilter, sortBy, sortDir, s, partner);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setAppliedSearch('');
    load(1, statusFilter, sortBy, sortDir, '', partner);
  };

  const handlePartnerChange = val => {
    setPartner(val);
    load(1, statusFilter, sortBy, sortDir, appliedSearch, val);
  };

  const handleExport = () => {
    const hdr = ['LMS ID', 'Learner Name', 'Partner', 'Status', 'Required Completed', 'Required Total'];
    const csv = [hdr.join(','), ...rows.map(r => [
      `"${r.lms_id}"`,
      `"${r.learner_name || ''}"`,
      `"${r.partner_name || ''}"`,
      r.status,
      r.required_completed ?? 0,
      r.required_total     ?? 0,
    ].join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `journey_${journeyId}_enrollments.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="tab-content-enrollments">
      {/* Journey-level progress summary */}
      {stats.enrolled_learners > 0 && (() => {
        const total      = stats.enrolled_learners || 0;
        const completed  = stats.completions       || 0;
        const inProgress = stats.in_progress_count || 0;
        const notStarted = stats.not_started_count || 0;
        const pct   = total > 0 ? Math.round((completed  / total) * 100) : 0;
        const ipPct = total > 0 ? Math.round((inProgress / total) * 100) : 0;
        return (
          <div
            data-testid="enrollments-progress-summary"
            className="report-card"
            style={{ marginBottom: 16, padding: '14px 20px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              {/* Main fraction */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexShrink: 0 }}>
                <span style={{ fontSize: 28, fontWeight: 800, color: '#15803d', lineHeight: 1 }}>
                  {completed.toLocaleString()}
                </span>
                <span style={{ fontSize: 15, color: '#94a3b8', fontWeight: 600 }}>
                  / {total.toLocaleString()} learners completed
                </span>
              </div>

              {/* Progress bar */}
              <div style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', display: 'flex' }}>
                  <div style={{ width: `${pct}%`,   background: '#22c55e', transition: 'width .4s ease' }} />
                  <div style={{ width: `${ipPct}%`, background: '#93c5fd', transition: 'width .4s ease' }} />
                </div>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>{pct}% complete</span>
              </div>

              {/* Stat chips removed — shown in KPI tiles above */}
            </div>
          </div>
        );
      })()}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        {/* Live data badge */}
        {dataSource === 'app1' && (
          <span
            data-testid="data-source-live"
            title="Progress data served live from LMS API"
            style={{
              padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
              background: '#dcfce7', color: '#15803d', letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Live
          </span>
        )}

        {/* Search input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <input
            data-testid="enrollments-search-input"
            type="text"
            placeholder="Search name or LMS ID…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()}
            style={{
              height: 34, padding: '0 10px', fontSize: 13, width: 200,
              border: '1px solid #e2e8f0', borderRight: 'none',
              borderRadius: '7px 0 0 7px', outline: 'none', background: '#fff',
            }}
          />
          {appliedSearch && (
            <button
              data-testid="enrollments-search-clear"
              onClick={handleSearchClear}
              title="Clear search"
              style={{
                height: 34, padding: '0 8px', border: '1px solid #e2e8f0', borderRight: 'none',
                background: '#f8fafc', cursor: 'pointer', color: '#64748b', fontSize: 14,
              }}
            >
              ×
            </button>
          )}
          <button
            data-testid="enrollments-search-btn"
            onClick={handleSearchSubmit}
            style={{
              height: 34, padding: '0 12px', fontSize: 12, fontWeight: 600,
              background: '#6366f1', color: '#fff', border: 'none',
              borderRadius: '0 7px 7px 0', cursor: 'pointer',
            }}
          >
            Search
          </button>
        </div>

        {/* Partner filter */}
        {partners.length > 0 && (
          <select
            data-testid="enrollments-partner-filter"
            value={partner}
            onChange={e => handlePartnerChange(e.target.value)}
            style={{
              height: 34, padding: '0 10px', fontSize: 13,
              border: '1px solid #e2e8f0', borderRadius: 7, outline: 'none', background: '#fff',
              maxWidth: 200,
            }}
          >
            <option value="">All Partners</option>
            {partners.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        )}

        <select
          data-testid="enrollment-status-filter"
          value={statusFilter}
          onChange={e => handleStatusChange(e.target.value)}
          style={{
            height: 34, padding: '0 10px', fontSize: 13,
            border: '1px solid #e2e8f0', borderRadius: 7, outline: 'none', background: '#fff',
          }}
        >
          <option value="all">All Status</option>
          <option value="not_started">
            Not Started{stats?.not_started_count != null ? ` (${stats.not_started_count.toLocaleString()})` : ''}
          </option>
          <option value="in_progress">
            In Progress{stats?.in_progress_count != null ? ` (${stats.in_progress_count.toLocaleString()})` : ''}
          </option>
          <option value="completed">
            Completed{stats?.completions != null ? ` (${stats.completions.toLocaleString()})` : ''}
          </option>
        </select>
        <span style={{ color: '#64748b', fontSize: 13 }}>{total.toLocaleString()} learners</span>
        <ExportBtn onClick={handleExport} testId="enrollments-export-btn" />
      </div>

      {/* Table */}
      <div className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="report-table" data-testid="enrollments-table">
            <thead>
              <tr>
                <th>LMS ID</th>
                <th>Learner Name</th>
                <th>Partner</th>
                <th>Status</th>
                <th
                  data-testid="th-required-progress"
                  style={{ minWidth: 150, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                  onClick={() => handleSort('required_completed')}
                  title="Sort by required progress"
                >
                  Required Progress{' '}
                  {sortBy === 'required_completed'
                    ? (sortDir === 'desc' ? '↓' : '↑')
                    : <span style={{ color: '#cbd5e1', fontSize: 11 }}>↕</span>}
                </th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8' }}>Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8' }}>No enrollments found.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={r.lms_id || i} data-testid={`enrollment-row-${i}`}>
                  <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#64748b' }}>{r.lms_id}</td>
                  <td style={{ fontWeight: 600, color: '#1e293b' }}>
                    {r.learner_name || <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.partner_name || '—'}
                  </td>
                  <td><StatusBadge status={r.status} /></td>
                  <td style={{ minWidth: 150 }}>
                    <RequiredProgressCell
                      requiredCompleted={r.required_completed ?? 0}
                      requiredTotal={r.required_total ?? 0}
                    />
                  </td>
                  <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                    <button
                      data-testid={`view-activity-${i}`}
                      title="View activity progress"
                      onClick={() => navigate(`/reports/journeys/${journeyId}/learners/${r.lms_id}`)}
                      style={{
                        background: '#f1f5f9', border: 'none', borderRadius: 6,
                        padding: '4px 7px', cursor: 'pointer', color: '#6366f1',
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        fontSize: 11, fontWeight: 600,
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#e0e7ff'}
                      onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
                    >
                      <Activity size={12} /> Activity
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onPage={p => load(p, statusFilter, sortBy, sortDir, appliedSearch, partner)} />
      </div>
    </div>
  );
}

// ── Tab 2: Badge Earners ──────────────────────────────────────────────────────
function BadgeEarnersTab({ journeyId }) {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/journeys/${journeyId}/badge-earners`, { withCredentials: true })
      .then(r => setRows(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [journeyId]);

  const handleExport = () => {
    const hdr = ['LMS ID', 'Learner Name', 'Partner', 'Badge'];
    const csv = [hdr.join(','), ...rows.map(r => [
      `"${r.lms_id}"`,
      `"${r.learner_name || ''}"`,
      `"${r.partner_name || ''}"`,
      `"${r.badge_name || ''}"`,
    ].join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `journey_${journeyId}_badge_earners.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="tab-content-badges">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ color: '#64748b', fontSize: 13 }}>{rows.length.toLocaleString()} badge earners</span>
        <ExportBtn onClick={handleExport} testId="badge-earners-export-btn" />
      </div>

      <div className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="report-table" data-testid="badge-earners-table">
            <thead>
              <tr>
                <th>LMS ID</th>
                <th>Learner Name</th>
                <th>Partner</th>
                <th>Badge</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8' }}>Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8' }}>No badge earners for this journey.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={r.lms_id || i} data-testid={`badge-earner-row-${i}`}>
                  <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#64748b' }}>{r.lms_id}</td>
                  <td style={{ fontWeight: 600, color: '#1e293b' }}>
                    {r.learner_name || <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.partner_name || '—'}
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Award size={13} color="#f59e0b" />
                      <span style={{ fontSize: 12, color: '#1e293b' }}>{r.badge_name || '—'}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function JourneyDetailPage() {
  const { journeyId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState('enrollments');
  const [sortBy, setSortBy]         = useState(null);
  const [sortDir, setSortDir]       = useState('desc');
  const [filterSummary, setFilterSummary] = useState(null); // null = show overall stats

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/journeys/${journeyId}/detail`, { withCredentials: true })
      .then(r => setMeta(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [journeyId]);

  const stats = meta?.enrollment_summary || {};

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Back nav */}
      <button
        data-testid="back-to-journeys-btn"
        onClick={() => navigate('/reports/journeys')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 20,
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 13, color: '#6366f1', fontWeight: 600, padding: 0,
        }}
      >
        <ArrowLeft size={14} /> All Journeys
      </button>

      {loading ? (
        <div style={{ color: '#94a3b8', textAlign: 'center', paddingTop: 48 }}>Loading journey…</div>
      ) : !meta ? (
        <div style={{ color: '#ef4444', textAlign: 'center', paddingTop: 48 }}>Journey not found.</div>
      ) : (
        <>
          {/* Header */}
          <div style={{ marginBottom: 20 }}>
            <h1
              data-testid="journey-detail-title"
              style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}
            >
              {meta.name}
            </h1>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {meta.solution_area && (
                <span style={{ fontSize: 13, color: '#64748b' }}>
                  <span style={{ fontWeight: 600, color: '#374151' }}>Solution Area: </span>{meta.solution_area}
                </span>
              )}
              {meta.project && (
                <span style={{ fontSize: 13, color: '#64748b' }}>
                  <span style={{ fontWeight: 600, color: '#374151' }}>Project: </span>{meta.project}
                </span>
              )}
              {meta.target_role && (
                <span style={{ fontSize: 13, color: '#64748b' }}>
                  <span style={{ fontWeight: 600, color: '#374151' }}>Role: </span>{meta.target_role}
                </span>
              )}
            </div>
          </div>

          {/* KPIs — priority: filterSummary (active filter) > enrollment_summary from detail API */}
          <div className="stats-grid" data-testid="journey-detail-kpis" style={{ marginBottom: 28 }}>
            {filterSummary && (
              <div style={{ gridColumn: '1 / -1', marginBottom: -8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#6366f1', background: '#eef2ff', padding: '2px 10px', borderRadius: 10 }}>
                  Filtered view
                </span>
              </div>
            )}
            {(() => {
              const src = filterSummary;
              return (<>
                <KpiCard label="Total Enrollments" value={fmt(src ? src.total       : stats.enrolled_learners)} accent="#6366f1" testId="kpi-total-enrolled" />
                <KpiCard label="Not Started"        value={fmt(src ? src.not_started : stats.not_started_count)} accent="#94a3b8" testId="kpi-not-started" />
                <KpiCard label="In Progress"        value={fmt(src ? src.in_progress : stats.in_progress_count)} accent="#f59e0b" testId="kpi-in-progress" />
                <KpiCard label="Completed"          value={fmt(src ? src.completed   : stats.completions)}        accent="#22c55e" testId="kpi-completed" />
              </>);
            })()}
          </div>

          {/* Tabs */}
          <div
            data-testid="detail-tabs"
            style={{ borderBottom: '2px solid #e2e8f0', display: 'flex', gap: 0, marginBottom: 20 }}
          >
            {[
              { id: 'enrollments', label: 'Learner Enrollments' },
              { id: 'badges',      label: 'Badge Earners' },
            ].map(tab => (
              <button
                key={tab.id}
                data-testid={`detail-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '8px 22px', fontSize: 13, fontWeight: 600,
                  border: 'none', background: 'none', cursor: 'pointer',
                  borderBottom: activeTab === tab.id ? '2px solid #6366f1' : '2px solid transparent',
                  color: activeTab === tab.id ? '#6366f1' : '#64748b',
                  marginBottom: -2, transition: 'color 0.15s',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'enrollments' && <EnrollmentsTab journeyId={journeyId} stats={stats} sortBy={sortBy} setSortBy={setSortBy} sortDir={sortDir} setSortDir={setSortDir} onFilterSummary={setFilterSummary} />}
          {activeTab === 'badges'      && <BadgeEarnersTab journeyId={journeyId} />}
        </>
      )}
    </div>
  );
}
